import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";
import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import DateInput from "@/components/shared/forms/DateInput";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";
import { type FAMI } from "@/models/fami.tsx";
import { useCoreData } from "@/queries.ts";
import {
  FaUsers,
  FaHome,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useFAMIFormLogic } from "../../useFAMIFormLogic";
import { validateMinDate } from "@/utils/util";
import { getStateOptionsCaliforniaFirst } from "@/utils/states";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";

type ServiceReasonOption = {
  Text: string;
  Value: string;
};

type ServiceReasonsMultiSelectProps = {
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled: boolean;
  options: ServiceReasonOption[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
  maxSelections?: number;
  placeholder?: string;
  resetKey: string;
  errorMessage?: string;
  afterChange?: () => void;
  isRequired?: boolean;
};

const mapInitialValues = (
  initialValues: string[],
  options: ServiceReasonOption[],
  maxSelections: number
): ServiceReasonOption[] => {
  return initialValues
    .filter(Boolean)
    .map((val) => options.find((o) => o.Value === val))
    .filter((o): o is ServiceReasonOption => !!o)
    .slice(0, maxSelections);
};

function ServiceReasonsMultiSelect({
  id,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  options,
  initialValues,
  onChangeValues,
  maxSelections = 9,
  placeholder = "Select up to nine reasons",
  resetKey,
  errorMessage,
  afterChange,
  isRequired,
}: ServiceReasonsMultiSelectProps) {
  const [selected, setSelected] = useState<ServiceReasonOption[]>(() =>
    mapInitialValues(initialValues, options, maxSelections)
  );

  useEffect(() => {
    setSelected(mapInitialValues(initialValues, options, maxSelections));
  }, [resetKey, initialValues, options, maxSelections]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as ServiceReasonOption[]) ?? [];
    const limited = next.slice(0, maxSelections);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
    afterChange?.();
  };

  const effectiveAriaLabel =
    isRequired && ariaLabel ? `${ariaLabel} (required)` : ariaLabel;

  useMultiSelectChipListLabel(id, effectiveAriaLabel, selected);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      ariaLabel={effectiveAriaLabel}
      ariaDescribedBy={ariaDescribedBy}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={placeholder}
      valid={!errorMessage}
      validationMessage={errorMessage}
      inputAttributes={errorMessage ? { "aria-invalid": true } : undefined}
      required={isRequired}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

const ADDRESS_CHARS_REGEX = /^[A-Za-z0-9À-ÖØ-öø-ÿ.\-'/ ]*$/;
const ADDRESS_CHARS_MESSAGE =
  "Only letters, numbers, periods, hyphens, apostrophes, forward slashes, and spaces are allowed";

function validateAddressChars(value?: string) {
  return !value || ADDRESS_CHARS_REGEX.test(value) ? true : ADDRESS_CHARS_MESSAGE;
}

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type FAMIWizardFormProps = {
  form: UseFormReturn<FAMI, any, FAMI>;
  preschools: PreschoolSummary[];
  familyNumber?: number;
  onRemove?: () => void;
  footer?: React.ReactNode;
  hideAddress?: boolean;
};

export function FAMIWizardForm({
  form,
  preschools,
  familyNumber = 1,
  onRemove,
  footer,
  hideAddress = false,
}: FAMIWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("FAMI");
  const coreData = useCoreData();

  const {
    register,
    formState: { errors },
    watch,
  } = form;

  const resetKey = `wizard-new-family-${familyNumber}`;
  const isEdit = false;

  const formValues = watch();
  const family = {
    ...formValues,
    EffectiveStartDate: formValues.EffectiveStartDate || "",
  };

  const {
    showMailingAddress,
    handleMailingToggle,
    isSecondHohRequired,
    requireResidentialAddress1,
    requireResidentialCityStateZip,
    requireMailingCityStateZip,
    isExperiencingHomelessness,
    getServiceReasons,
    setServiceReasons,
    hasAnyServiceReasonSelected,
    validateCalWorksRecipient,
  } = useFAMIFormLogic({
    form,
    family,
    resetKey,
    isEdit,
    reasonForServiceList: coreData.reasonForServiceList,
    calWORKsList: coreData.calWORKsList,
  });

  const stateOptions = getStateOptionsCaliforniaFirst(coreData.states);

  const [familyCollapsed, setFamilyCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (!hasErrors) return;

    const familyFields = [
      "EffectiveStartDate",
      "LocalFamilyId",
      "PreschoolCode",
      "FamilySize",
      "FamilyIncome",
      "CALWorksRecipient",
      "HeadOfHousehold1FirstName",
      "HeadOfHousehold1LastName",
      "HeadOfHousehold1NeedForService1",
      "HeadOfHousehold1HighestEducationLevel",
      "HeadOfHousehold2FirstName",
      "HeadOfHousehold2LastName",
      "HeadOfHousehold2NeedForService1",
      "HeadOfHousehold2HighestEducationLevel",
      "HeadOfHouseholdWrittenCommunicationPreference",
      "HeadOfHouseholdVerbalCommunicationPreference",
    ];

    const errorKeys = Object.keys(errors);
    if (errorKeys.some((k) => familyFields.includes(k))) {
      setFamilyCollapsed(false);
    }
  }, [hasErrors, errors]);

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    userSelect: "none" as const,
  };

  const Wrapper = familyNumber === 1 ? Container : "div";
  const wrapperProps =
    familyNumber === 1
      ? {
          className: "ps-3 pe-3 overview-container",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {};

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? {
                border: "2px dashed #c5ccd4",
                borderRadius: "8px",
              }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(familyCollapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <h3 className="section-title mb-0 flex-grow-1">
            <button
              type="button"
              className="d-flex align-items-center gap-2 w-100"
              style={{
                border: "none",
                background: "none",
                font: "inherit",
                textAlign: "left" as const,
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setFamilyCollapsed((prev) => !prev)}
              aria-expanded={!familyCollapsed}
            >
              <FaUsers className="section-icon" aria-hidden="true" />
              {familyNumber === 1
                ? "Family Information"
                : `Family ${familyNumber} Information`}
            </button>
          </h3>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#dc3545",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                  cursor: "pointer",
                }}
                onClick={onRemove}
                aria-label={
                  familyNumber === 1
                    ? "Remove Family Information"
                    : `Remove Family ${familyNumber} Information`
                }
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setFamilyCollapsed((prev) => !prev)}
              aria-expanded={!familyCollapsed}
              aria-label={
                familyCollapsed
                  ? familyNumber === 1
                    ? "Expand Family Information"
                    : `Expand Family ${familyNumber} Information`
                  : familyNumber === 1
                    ? "Collapse Family Information"
                    : `Collapse Family ${familyNumber} Information`
              }
            >
              {familyCollapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!familyCollapsed && (
          <div style={{ padding: "20px" }}>
            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="EffectiveStartDate"
                  labelText="Effective Start Date"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <DateInput
                      id={id}
                      disabled={false}
                      max="9999-12-31"
                      {...register(name, {
                        required: "Effective start date is required",
                        validate: validateMinDate,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="LocalFamilyId"
                  labelText="Family Local ID (FICN)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={30}
                      {...register(name, {
                        required: "Family Local ID (FICN) is required",
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="PreschoolCode"
                  labelText="Preschool"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={form.control}
                      name={name}
                      rules={{ required: "Preschool is required" }}
                      options={preschools.map((x) => ({
                        Text: x.PreschoolName,
                        Value: x.PreschoolCDSCode,
                      }))}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="FamilySize"
                  labelText="Family Size"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      inputMode="numeric"
                      onKeyDown={(e) => {
                        if (
                          !/^\d$/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "ArrowLeft",
                            "ArrowRight",
                            "Tab",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      {...register(name, {
                        required: "Family Size is required",
                        validate: (value) => {
                          const n = Number(value);
                          if (!Number.isInteger(n))
                            return "Family Size must be a whole number";
                          if (n < 1) return "Family Size must be at least 1";

                          const formValues = form.getValues();
                          const hasHoh2 =
                            formValues.HeadOfHousehold2FirstName?.trim() ||
                            formValues.HeadOfHousehold2LastName?.trim();

                          if (hasHoh2 && n < 3) {
                            return "Family Size must be at least 3 when a second head of household is entered";
                          }

                          return true;
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="FamilyIncome"
                  labelText="Family Monthly Income"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      inputMode="numeric"
                      onKeyDown={(e) => {
                        if (
                          !/^\d$/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "ArrowLeft",
                            "ArrowRight",
                            "Tab",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      {...register(name, {
                        required: "Income is required",
                        validate: (value) => {
                          const n = Number(value);
                          if (!Number.isInteger(n))
                            return "Income must be a whole number";
                          if (n < 0 || n > 99999)
                            return "Income must be between 0 and 99999";
                          return true;
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="CALWorksRecipient"
                  labelText="CalWORKs Recipient"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={false}
                      {...register(name, {
                        required: "CalWORKs recipient is required",
                        validate: validateCalWorksRecipient,
                      })}
                      options={coreData.calWORKsList}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <br />
            <h4>1st Head of Household</h4>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold1FirstName"
                  labelText="First Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={30}
                      {...register(name, {
                        required: "First Name is required",
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold1MiddleName"
                  labelText="Middle Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={30}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold1LastName"
                  labelText="Last Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={50}
                      {...register(name, { required: "Last Name is required" })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHousehold1NeedForService1"
                  labelText="Reasons For Needing Service"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name, validationProps) => (
                    <div>
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService1", {
                          validate: () =>
                            hasAnyServiceReasonSelected("HeadOfHousehold1")
                              ? true
                              : "Reasons for needing service is required",
                        })}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService2")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService3")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService4")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService5")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService6")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService7")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService8")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold1NeedForService9")}
                      />
                      <ServiceReasonsMultiSelect
                        id={id}
                        ariaLabel="Reasons For Needing Service (1st Head of Household)"
                        ariaDescribedBy={validationProps["aria-describedby"]}
                        key={`${resetKey}:hoh1`}
                        disabled={false}
                        isRequired
                        resetKey={resetKey}
                        options={coreData.reasonForServiceList}
                        initialValues={getServiceReasons("HeadOfHousehold1")}
                        maxSelections={9}
                        placeholder="Select up to nine reasons"
                        onChangeValues={(values) =>
                          setServiceReasons("HeadOfHousehold1", values)
                        }
                        errorMessage={
                          errors.HeadOfHousehold1NeedForService1?.message as
                            | string
                            | undefined
                        }
                        afterChange={() =>
                          form.trigger("HeadOfHousehold1NeedForService1")
                        }
                      />
                    </div>
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHousehold1HighestEducationLevel"
                  labelText="Highest Education Level"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={false}
                      {...register(name, {
                        required: "Highest Education Level is required",
                      })}
                      options={coreData.educationList}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <br />

            <div className="d-flex align-items-center justify-content-between">
              <h4 className="mb-0">2nd Head of Household</h4>
            </div>

            <div className="mt-3 row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold2FirstName"
                  labelText="First Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired={isSecondHohRequired}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={30}
                      {...register(name, {
                        validate: (value) => {
                          if (isSecondHohRequired && !value?.trim()) {
                            return "First Name is required";
                          }
                          return true;
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold2MiddleName"
                  labelText="Middle Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={30}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="HeadOfHousehold2LastName"
                  labelText="Last Name"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired={isSecondHohRequired}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={false}
                      maxLength={50}
                      {...register(name, {
                        validate: (value) => {
                          if (isSecondHohRequired && !value?.trim()) {
                            return "Last Name is required";
                          }
                          return true;
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHousehold2NeedForService1"
                  labelText="Reasons For Needing Service"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired={isSecondHohRequired}
                >
                  {(id, name, validationProps) => (
                    <div>
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService1", {
                          validate: () => {
                            if (!isSecondHohRequired) return true;
                            return hasAnyServiceReasonSelected(
                              "HeadOfHousehold2"
                            )
                              ? true
                              : "Reasons For Needing Service is required";
                          },
                        })}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService2")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService3")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService4")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService5")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService6")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService7")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService8")}
                      />
                      <input
                        type="hidden"
                        {...register("HeadOfHousehold2NeedForService9")}
                      />

                      <ServiceReasonsMultiSelect
                        id={id}
                        ariaLabel="Reasons For Needing Service (2nd Head of Household)"
                        ariaDescribedBy={validationProps["aria-describedby"]}
                        key={`${resetKey}:hoh2`}
                        disabled={false}
                        isRequired={isSecondHohRequired}
                        resetKey={resetKey}
                        options={coreData.reasonForServiceList}
                        initialValues={getServiceReasons("HeadOfHousehold2")}
                        maxSelections={9}
                        placeholder="Select up to nine reasons"
                        onChangeValues={(values) =>
                          setServiceReasons("HeadOfHousehold2", values)
                        }
                        errorMessage={
                          errors.HeadOfHousehold2NeedForService1?.message as
                            | string
                            | undefined
                        }
                        afterChange={() =>
                          form.trigger("HeadOfHousehold2NeedForService1")
                        }
                      />
                    </div>
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHousehold2HighestEducationLevel"
                  labelText="Highest Education Level"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                  isRequired={isSecondHohRequired}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={false}
                      {...register(name, {
                        validate: (value) => {
                          if (isSecondHohRequired && !value) {
                            return "Highest Education Level is required";
                          }
                          return true;
                        },
                      })}
                      options={coreData.educationList}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <br />

            <h4>Communication Preference</h4>

            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHouseholdWrittenCommunicationPreference"
                  labelText="Written"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={false}
                      {...register(name)}
                      options={coreData.languagesList}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="HeadOfHouseholdVerbalCommunicationPreference"
                  labelText="Verbal"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  idPrefix={`family-${familyNumber}-`}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={false}
                      {...register(name)}
                      options={coreData.languagesList}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>
        )}
      </div>

      {!hideAddress && (
        <div
          className="form-card-modern mb-3"
          style={
            onRemove
              ? {
                  border: "2px dashed #c5ccd4",
                  borderRadius: "8px",
                }
              : undefined
          }
        >
          <div className="section-header">
            <FaHome className="section-icon" />
            <h3 className="section-title">
              {familyNumber === 1
                ? "Address Information"
                : `Address Information ${familyNumber}`}
            </h3>
          </div>

          <br />

          <h4>Home Address</h4>

          <div className="row g-3">
            <div className="col-md-6">
              <FormFieldRow
                name="ResidentialAddress1"
                labelText="Address Line 1"
                errors={errors}
                tooltipMap={tooltipMap}
                idPrefix={`family-${familyNumber}-`}
                labelCol={12}
                inputCol={12}
                isRequired={requireResidentialAddress1}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={false}
                    maxLength={60}
                    {...register(name, {
                      validate: (value) => {
                        if (!isExperiencingHomelessness && !value?.trim()) {
                          return "Address Line 1 is required";
                        }
                        return validateAddressChars(value);
                      },
                    })}
                  />
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-6">
              <FormFieldRow
                name="ResidentialAddress2"
                labelText="Address Line 2"
                errors={errors}
                tooltipMap={tooltipMap}
                idPrefix={`family-${familyNumber}-`}
                labelCol={12}
                inputCol={12}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={false}
                    maxLength={60}
                    {...register(name, { validate: validateAddressChars })}
                  />
                )}
              </FormFieldRow>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-4">
              <FormFieldRow
                name="ResidentialCity"
                labelText="City"
                errors={errors}
                tooltipMap={tooltipMap}
                idPrefix={`family-${familyNumber}-`}
                labelCol={12}
                inputCol={12}
                isRequired={requireResidentialCityStateZip}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={false}
                    maxLength={30}
                    {...register(name, {
                      validate: (value) => {
                        if (requireResidentialCityStateZip && !value?.trim()) {
                          return "City is required";
                        }
                        return validateAddressChars(value);
                      },
                    })}
                  />
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-4">
              <FormFieldRow
                name="ResidentialState"
                labelText="State"
                errors={errors}
                tooltipMap={tooltipMap}
                idPrefix={`family-${familyNumber}-`}
                labelCol={12}
                inputCol={12}
                isRequired={requireResidentialCityStateZip}
              >
                {(id, name) => (
                  <Select
                    id={id}
                    disabled={false}
                    {...register(name, {
                      validate: (value) => {
                        if (requireResidentialCityStateZip && !value) {
                          return "State is required";
                        }
                        return true;
                      },
                    })}
                    options={stateOptions}
                  />
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-4">
              <FormFieldRow
                name="ResidentialZip"
                labelText="Zip"
                errors={errors}
                tooltipMap={tooltipMap}
                idPrefix={`family-${familyNumber}-`}
                labelCol={12}
                inputCol={12}
                isRequired={requireResidentialCityStateZip}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={false}
                    maxLength={10}
                    inputMode="numeric"
                    onKeyDown={(e) => {
                      if (
                        !/[\d-]/.test(e.key) &&
                        ![
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    {...register(name, {
                      validate: (value) => {
                        if (!value?.trim()) {
                          return requireResidentialCityStateZip
                            ? "Zip is required"
                            : true;
                        }
                        return ZIP_REGEX.test(value)
                          ? true
                          : "Zip must be in the format 99999 or 99999-9999";
                      },
                    })}
                  />
                )}
              </FormFieldRow>
            </div>
          </div>

          <br />

          <div
            className="d-flex align-items-center mb-3 p-3 rounded"
            style={{
              backgroundColor: "#eef4ff",
              border: "1px solid #cfe2ff",
              borderLeft: "5px solid #0d6efd",
            }}
          >
            <input
              type="checkbox"
              id={`mailingDifferent-${familyNumber}`}
              className="form-check-input me-2"
              checked={showMailingAddress}
              onChange={(e) => handleMailingToggle(e.target.checked)}
              disabled={false}
            />
            <label
              htmlFor={`mailingDifferent-${familyNumber}`}
              className="form-check-label fw-semibold"
            >
              Mailing address is different from home address
            </label>
          </div>

          {showMailingAddress && (
            <>
              <h4>Mailing Address</h4>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormFieldRow
                    name="MailingAddress1"
                    labelText="Address Line 1"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    idPrefix={`family-${familyNumber}-`}
                    labelCol={12}
                    inputCol={12}
                    isRequired
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={60}
                        {...register(name, {
                          validate: (value) => {
                            if (!value?.trim()) {
                              return "Address Line 1 is required";
                            }
                            return validateAddressChars(value);
                          },
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>

                <div className="col-md-6">
                  <FormFieldRow
                    name="MailingAddress2"
                    labelText="Address Line 2"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    idPrefix={`family-${familyNumber}-`}
                    labelCol={12}
                    inputCol={12}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={60}
                        {...register(name, { validate: validateAddressChars })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <FormFieldRow
                    name="MailingCity"
                    labelText="City"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    idPrefix={`family-${familyNumber}-`}
                    labelCol={12}
                    inputCol={12}
                    isRequired={requireMailingCityStateZip}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={30}
                        {...register(name, {
                          validate: (value) => {
                            if (requireMailingCityStateZip && !value?.trim()) {
                              return "City is required";
                            }
                            return validateAddressChars(value);
                          },
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>

                <div className="col-md-4">
                  <FormFieldRow
                    name="MailingState"
                    labelText="State"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    idPrefix={`family-${familyNumber}-`}
                    labelCol={12}
                    inputCol={12}
                    isRequired={requireMailingCityStateZip}
                  >
                    {(id, name) => (
                      <Select
                        id={id}
                        disabled={false}
                        {...register(name, {
                          validate: (value) => {
                            if (requireMailingCityStateZip && !value) {
                              return "State is required";
                            }
                            return true;
                          },
                        })}
                        options={stateOptions}
                      />
                    )}
                  </FormFieldRow>
                </div>

                <div className="col-md-4">
                  <FormFieldRow
                    name="MailingZip"
                    labelText="Zip"
                    errors={errors}
                    tooltipMap={tooltipMap}
                    idPrefix={`family-${familyNumber}-`}
                    labelCol={12}
                    inputCol={12}
                    isRequired={requireMailingCityStateZip}
                  >
                    {(id, name) => (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={10}
                        inputMode="numeric"
                        onKeyDown={(e) => {
                          if (
                            !/[\d-]/.test(e.key) &&
                            ![
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        {...register(name, {
                          validate: (value) => {
                            if (!value?.trim()) {
                              return requireMailingCityStateZip
                                ? "Zip is required"
                                : true;
                            }
                            return ZIP_REGEX.test(value)
                              ? true
                              : "Zip must be in the format 99999 or 99999-9999";
                          },
                        })}
                      />
                    )}
                  </FormFieldRow>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {footer}
    </Wrapper>
  );
}
